import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listPendingDepositDeclarations,
  listPendingWithdrawalDeclarations,
  refreshUserWallet,
} from "../../api/services/depositDeclarationService";
import { SocketContext } from "../SocketContext";
import {
  DEPOSIT_HISTORY_PATH,
  DEPOSIT_HISTORY_REFRESH,
  USER_WALLET_REFRESH,
  WALLET_DEPOSIT_UPDATED,
  WALLET_WITHDRAWAL_UPDATED,
  WITHDRAW_HISTORY_PATH,
  WITHDRAW_HISTORY_REFRESH,
  depositDeclarationPath,
  parsePendingDeclarations,
  parsePendingWithdrawals,
  withdrawalDeclarationPath,
} from "../../ui/Pages/Order/depositDeclarationModel";
import "../../ui/Pages/Order/depositDeclaration.css";

function readAccessToken() {
  try {
    return localStorage.getItem("token") || sessionStorage.getItem("token");
  } catch {
    return null;
  }
}

function withdrawalEventId(payload) {
  const row = payload?.row && typeof payload.row === "object" ? payload.row : {};
  return row._id || row.withdrawal_id || payload?.withdrawal_id || payload?.transactionId || "";
}

export default function DepositDeclarationBanner() {
  const navigate = useNavigate();
  const { socket } = useContext(SocketContext) || {};
  const [pending, setPending] = useState({ count: 0, deposits: [] });
  const [pendingWithdrawals, setPendingWithdrawals] = useState({ count: 0, withdrawals: [] });

  const loadPending = useCallback(async () => {
    if (!readAccessToken()) {
      setPending({ count: 0, deposits: [] });
      return;
    }
    try {
      const res = await listPendingDepositDeclarations();
      const parsed = parsePendingDeclarations(res);
      if (!parsed.ok) return;
      setPending({ count: parsed.count, deposits: parsed.deposits });
    } catch {
      /* A failed refresh must not look like "no deposits" after a real count, and an empty 200 is not an error. */
    }
  }, []);

  const loadWithdrawalPending = useCallback(async () => {
    if (!readAccessToken()) {
      setPendingWithdrawals({ count: 0, withdrawals: [] });
      return;
    }
    try {
      const res = await listPendingWithdrawalDeclarations();
      const parsed = parsePendingWithdrawals(res);
      if (!parsed.ok) return;
      setPendingWithdrawals({ count: parsed.count, withdrawals: parsed.withdrawals });
    } catch {
      /* Same rule as deposits: a failed refresh keeps the last count. */
    }
  }, []);

  useEffect(() => {
    void loadPending();
    void loadWithdrawalPending();
    const onAuth = () => {
      void loadPending();
      void loadWithdrawalPending();
    };
    window.addEventListener("agce-auth-changed", onAuth);
    window.addEventListener("agce-auth-token-changed", onAuth);
    return () => {
      window.removeEventListener("agce-auth-changed", onAuth);
      window.removeEventListener("agce-auth-token-changed", onAuth);
    };
  }, [loadPending, loadWithdrawalPending]);

  useEffect(() => {
    if (!socket) return undefined;
    const onDeposit = (payload) => {
      if (!payload || payload.type !== "deposit") return;
      void loadPending();
      try {
        window.dispatchEvent(new CustomEvent(WALLET_DEPOSIT_UPDATED, { detail: payload }));
      } catch {
        /* ignore */
      }
      window.dispatchEvent(new CustomEvent(DEPOSIT_HISTORY_REFRESH));
      if (payload.status === "AWAITING_DECLARATION" && payload.deposit_id) {
        const next = depositDeclarationPath(payload.deposit_id);
        if (window.location.pathname !== next) navigate(next);
      }
      if (payload.status === "COMPLETED") {
        void refreshUserWallet().finally(() => {
          window.dispatchEvent(new CustomEvent(USER_WALLET_REFRESH));
        });
      }
    };
    const onWithdrawal = (payload) => {
      if (!payload || payload.type !== "withdrawal") return;
      const id = withdrawalEventId(payload);
      const status = String(payload.status || payload.row?.status || "").toUpperCase();
      void loadWithdrawalPending();
      try {
        window.dispatchEvent(
          new CustomEvent(WALLET_WITHDRAWAL_UPDATED, {
            detail: { ...payload, withdrawal_id: id, status },
          })
        );
      } catch {
        /* ignore */
      }
      window.dispatchEvent(new CustomEvent(WITHDRAW_HISTORY_REFRESH));
      if (status === "AWAITING_DECLARATION" && id) {
        const next = withdrawalDeclarationPath(id);
        if (window.location.pathname !== next) navigate(next);
      }
      if (status === "PENDING") {
        void refreshUserWallet().finally(() => {
          window.dispatchEvent(new CustomEvent(USER_WALLET_REFRESH));
        });
      }
    };
    socket.on("wallet:deposit:updated", onDeposit);
    socket.on("wallet:withdrawal:updated", onWithdrawal);
    return () => {
      socket.off("wallet:deposit:updated", onDeposit);
      socket.off("wallet:withdrawal:updated", onWithdrawal);
    };
  }, [socket, loadPending, loadWithdrawalPending, navigate]);

  const openFromBanner = () => {
    const firstId = pending.deposits[0] && pending.deposits[0].deposit_id;
    if (firstId) navigate(depositDeclarationPath(firstId));
    else navigate(DEPOSIT_HISTORY_PATH);
  };

  const openWithdrawalBanner = () => {
    const firstId = pendingWithdrawals.withdrawals[0] && pendingWithdrawals.withdrawals[0].withdrawal_id;
    if (firstId) navigate(withdrawalDeclarationPath(firstId));
    else navigate(WITHDRAW_HISTORY_PATH);
  };

  if (!readAccessToken()) return null;
  const showDeposit = pending.count > 0;
  const showWithdrawal = pendingWithdrawals.count > 0;
  if (!showDeposit && !showWithdrawal) return null;

  return (
    <>
      {showDeposit ? (
        <button type="button" className="dd-banner" onClick={openFromBanner}>
          You have a deposit waiting for confirmation.
        </button>
      ) : null}
      {showWithdrawal ? (
        <button type="button" className="dd-banner" onClick={openWithdrawalBanner}>
          You have a withdrawal waiting for confirmation.
        </button>
      ) : null}
    </>
  );
}
